import { useContext, useEffect } from 'react';
import NoSSR from 'react-no-ssr';
import Router from 'next/router';
import { Field } from 'formik';
import Link from 'next/link';

import ProgressBar from '../ProgressBar/ProgressBar';
import InputRadio from '../InputRadio/InputRadio';
import InputText from '../InputText/InputText';
import { FormContext } from '../../context/FormContext';
import useKeyPress from '../../hooks/useKeyPress';
import { identify, group } from '../../client/segment';
import {
  enrich,
  collectCompanyFields,
  collectPersonFields,
  enrichCompany,
  triggerWithTimeout,
} from '../../client/clearbit';
import { dig } from '../../helpers/dig';
import {
  QUALIFICATIONS,
  USE_CASES,
  getQualification,
  VISITOR_OPTIONS,
  getValueKeyForStep,
  meetsSkipStepRequirements,
  completeEmailStep,
  domainForm,
  completeDomainStep,
  completeUseCaseStep,
  completeMonthlyVisitorsStep,
  deleteEmptyValues,
  completeBooking,
} from './helpers';
import { UserContext } from '../../context/UserContext';
import { analyticsClient } from '../../client/analytics';
import ChiliPiperWidget from './ChiliPiperWidget';

const EVENT_SOURCE = 'signup-form';

const Form = ({ values, setFieldValue, errors }) => {
  const formState = useContext(FormContext);
  const userState = useContext(UserContext);
  const { email, domain, useCase, monthlyVisitors } = values;
  const { step, totalSteps, latestEnrichResult, foundEmailMatch, qualification } = formState;

  const hasErrors = errors[getValueKeyForStep(step)];
  const value = values[getValueKeyForStep(step)];
  const cansubmit = step < totalSteps && !hasErrors && value && value.length > 0;

  const handleGoBack = () => {
    formState.setStep(step > 1 ? step - 1 : 1);
  };

  const handleSubmit = () => {
    if (step === 1) {
      completeEmailStep(email, userState, EVENT_SOURCE);

      triggerWithTimeout(
        () => enrich(email),
        () => {
          setFieldValue('domain', '');
          formState.setStep(step + 1);
        },
        (result) => {
          formState.setLatestEnrichResult(result);
          formState.setStep(step + 1);
        },
        (result) => {
          setFieldValue('domain', dig(result, 'company', 'domain'));
          formState.setFoundEmailMatch(true);
          analyticsClient.track('Clearbit email enriched', {
            email,
            domain: dig(result, 'company', 'domain'),
            source: EVENT_SOURCE,
          });
        },
      );
      return;
    }

    if (step === 2) {
      if (!foundEmailMatch || dig(latestEnrichResult, 'company', 'domain') !== domainForm(domain)) {
        // TODO: should we send a group as soon as this completes?
        enrichCompany(domain).then((result) => {
          formState.setLatestEnrichResult({
            company: result,
          });
          analyticsClient.track('Clearbit domain enriched', {
            domain,
            source: EVENT_SOURCE,
          });
        });
      } else {
        identify(email, {
          email,
          referrer_url: userState.referrer,
          clearbit_enriched: 'true',
          ...deleteEmptyValues(collectPersonFields(latestEnrichResult)),
        });
        analyticsClient.identify(email, {
          email,
          referrer_url: userState.referrer,
          clearbit_enriched: 'true',
          ...deleteEmptyValues(collectPersonFields(latestEnrichResult)),
        });
      }
      completeDomainStep(domain, formState);
      formState.setStep(step + 1);
    } else if ((step === 3 && meetsSkipStepRequirements(latestEnrichResult)) || step === totalSteps - 1) {
      // skip the employees question if employees size is large enough
      const evaluatedQualification = getQualification(latestEnrichResult, monthlyVisitors, useCase);
      identify(email, {
        email,
        use_case: useCase,
        monthly_visitors: monthlyVisitors,
        contact_pre_qualification: evaluatedQualification,
      });
      analyticsClient.identify(email, {
        email,
        use_case: useCase,
        monthly_visitors: monthlyVisitors,
        contact_pre_qualification: evaluatedQualification,
      });
      formState.setQualification(evaluatedQualification);
      if (step === 3) {
        completeUseCaseStep(useCase);
        completeMonthlyVisitorsStep(monthlyVisitors, true);
      } else {
        completeMonthlyVisitorsStep(monthlyVisitors, false);
      }
      if (evaluatedQualification === QUALIFICATIONS.unqualified) {
        completeBooking(evaluatedQualification, true);
        setTimeout(() => Router.push('/group-demo'), 3000);
      }
      formState.setStep(totalSteps);
    } else {
      completeUseCaseStep(useCase);
      formState.setStep(step + 1);
    }

    const parsedDomain = domainForm(domain);
    const website = parsedDomain ? `https://${parsedDomain}` : domain;

    group(
      email,
      website,
      deleteEmptyValues({
        use_case: useCase,
        monthly_visitors: monthlyVisitors,
        website,
        email,
        ...collectCompanyFields(latestEnrichResult),
      }),
    );
  };

  // Listen to Enter key
  const enterKey = useKeyPress('Enter');

  // Remove background color or nav-fixed css
  useEffect(() => {
    document.body.classList.remove('PageWrapper--frostBlue', 'has-scrolled');
  }, []);

  // Use effect hook to list to Enter key press
  useEffect(() => {
    if (enterKey && cansubmit) {
      handleSubmit();
    }
  }, [enterKey]);

  const revealedDomain = userState.revealResult && userState.revealResult.domain;
  const loadingReveal = userState.loadingReveal;

  // this ensures the placeholder doesn't change if reveal completes
  // TODO: we should have some kind of short loading animation or even a fade in to avoid the jarring snap
  const defaultPlaceholder = loadingReveal ? '' : 'your@email.com';
  const renderBooking = qualification === QUALIFICATIONS.strong || qualification === QUALIFICATIONS.weak;

  return (
    <div className={`Form ${step === totalSteps ? 'is-complete' : ''}`}>
      <div className="Form-inner">
        <div className="Form-content">
          <Link href="/">
            <a className="Form-logo">
              <svg>
                <use xlinkHref="#logo-mutiny-text" />
              </svg>
            </a>
          </Link>

          <div className="Form-step">
            <div className="Form-stepContent">
              {step === 1 && (
                <>
                  <h2>Enter your work email</h2>
                  <Field
                    name="email"
                    render={({ field }) => (
                      <InputText
                        placeholder={revealedDomain ? `you@${revealedDomain}` : defaultPlaceholder}
                        type="email"
                        {...field}
                      />
                    )}
                  />
                </>
              )}

              {step === 2 && (
                <>
                  <h2>Enter your website URL</h2>
                  <Field
                    name="domain"
                    render={({ field }) => <InputText placeholder="Your website" type="text" {...field} />}
                  />
                </>
              )}

              {step === 3 && (
                <>
                  <h2>What’s your primary use case?</h2>
                  <Field
                    name="useCase"
                    render={({ field }) => (
                      <InputRadio
                        label="Increase main website conversion rate"
                        id={USE_CASES.websiteConversion}
                        {...field}
                      />
                    )}
                  />
                  <Field
                    name="useCase"
                    render={({ field }) => (
                      <InputRadio
                        label="Increase paid landing page conversion rate"
                        id={USE_CASES.landingPageConversion}
                        {...field}
                      />
                    )}
                  />
                  <Field
                    name="useCase"
                    render={({ field }) => (
                      <InputRadio
                        label="Engage ABM target accounts with personalized experiences"
                        id={USE_CASES.abm}
                        {...field}
                      />
                    )}
                  />
                </>
              )}

              {step === 4 && (
                <>
                  <h2>How many monthly unique visitors do you have?</h2>
                  <Field
                    name="monthlyVisitors"
                    render={({ field }) => (
                      <InputRadio label="Less than 10k" id={VISITOR_OPTIONS.under10k} {...field} />
                    )}
                  />
                  <Field
                    name="monthlyVisitors"
                    render={({ field }) => <InputRadio label="10k – 50k" id={VISITOR_OPTIONS.under50k} {...field} />}
                  />
                  <Field
                    name="monthlyVisitors"
                    render={({ field }) => <InputRadio label="50k – 250k" id={VISITOR_OPTIONS.under250k} {...field} />}
                  />
                  <Field
                    name="monthlyVisitors"
                    render={({ field }) => <InputRadio label="Over 250k" id={VISITOR_OPTIONS.over250k} {...field} />}
                  />
                </>
              )}

              {step === totalSteps && (
                <>
                  {qualification && qualification !== QUALIFICATIONS.unqualified ? (
                    <h2>Looks like you are a perfect fit for Mutiny. Book a time to learn more.</h2>
                  ) : (
                    <h2>Thank you! You'll be redirected in a moment to sign up for a demo.</h2>
                  )}
                </>
              )}
              <NoSSR>
                <div key={`cta-${step}`} className={`Form-navigation ${cansubmit ? 'is-visible' : ''}`}>
                  <a
                    className="Form-next"
                    style={{ cursor: cansubmit ? 'pointer' : 'default' }}
                    onClick={() => cansubmit && handleSubmit()}
                  >
                    <span>Ok</span>
                    <svg>
                      <use xlinkHref="#arrow-next" />
                    </svg>
                  </a>
                  <span className="Form-navigationEnter">Press ENTER</span>
                </div>
              </NoSSR>
            </div>
          </div>
        </div>

        <div className={`Form-calendar ${renderBooking ? 'is-visible' : ''}`}>
          {renderBooking && (
            <ChiliPiperWidget enrichResults={latestEnrichResult} values={values} qualification={qualification} />
          )}
        </div>

        <ul className="Form-footer">
          <li>
            {step !== 1 && totalSteps !== step && (
              <a onClick={() => handleGoBack()} className="Form-previous">
                <svg>
                  <use xlinkHref="#arrow-back" />
                </svg>
                <span>Back</span>
              </a>
            )}
          </li>
          <li>{step > 1 && totalSteps !== step && <ProgressBar step={step} total={totalSteps} />}</li>
          <li />
        </ul>
      </div>
    </div>
  );
};

export default Form;
